import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Send as SendIcon,
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  CheckCircle as ApproveIcon,
  RateReview as ReviewIcon,
  Warning as NeedsChangesIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import PlanificationService from '../../services/planification.service';
import PlanificationTypeService from '../../services/planificationType.service';
import CursoService from '../../services/curso.service';
import UserService from '../../services/user.service';

const statusColor = (status) => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'sent':
      return 'info';
    case 'in_review':
      return 'warning';
    case 'needs_changes':
      return 'error';
    case 'approved':
      return 'success';
    default:
      return 'default';
  }
};

const PlanificationForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const typeIdParam = searchParams.get('planification_type_id') || '';
  const preselectedTypeId = typeIdParam ? (Number(typeIdParam) || typeIdParam) : '';
  const parentIdParam = searchParams.get('parent_id') || '';
  const preselectedParentId = parentIdParam ? (Number(parentIdParam) || parentIdParam) : '';

  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [planifications, setPlanifications] = useState([]);
  const [parentPlan, setParentPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const [users, setUsers] = useState([]);
  const [addUserId, setAddUserId] = useState('');
  const [addPermission, setAddPermission] = useState('view');
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [form, setForm] = useState({
    planification_type_id: preselectedTypeId,
    parent_id: preselectedParentId || null,
    curso_id: null,
    name: '',
    start_date: '',
    end_date: '',
  });
  const [sectionDrafts, setSectionDrafts] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentNeedsChanges, setCommentNeedsChanges] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchTypes();
    fetchCursos();
  }, []);

  const fetchCursos = async () => {
    try {
      const res = await CursoService.getAll();
      setCursos(res.data?.data || []);
    } catch (e) {
      console.error('Error loading cursos:', e);
    }
  };

  useEffect(() => {
    if (!isEdit && preselectedParentId) {
      PlanificationService.get(preselectedParentId)
        .then((res) => setParentPlan(res.data?.data))
        .catch(() => setParentPlan(null));
    }
  }, [isEdit, preselectedParentId]);

  useEffect(() => {
    if (isEdit) {
      loadPlan();
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit) {
      PlanificationService.getAll()
        .then((res) => setPlanifications(res.data?.data || []))
        .catch(() => setPlanifications([]));
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      UserService.getAll()
        .then((res) => setUsers(res.data?.data || res.data || []))
        .catch(() => setUsers([]));
    }
  }, [isEdit]);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchTypes = async () => {
    try {
      const res = await PlanificationTypeService.getAll();
      setTypes(res.data?.data || []);
    } catch (e) {
      console.error('Error loading types:', e);
    }
  };

  const loadPlan = async () => {
    setLoading(true);
    try {
      const res = await PlanificationService.get(id);
      const p = res.data?.data;
      setPlan(p);
      setForm({
        planification_type_id: p.planification_type_id || p.type?.id || '',
        parent_id: p.parent_id ?? p.parent?.id ?? null,
        curso_id: p.curso_id ?? p.curso?.id ?? null,
        name: p.name || '',
        start_date: p.start_date || '',
        end_date: p.end_date || '',
      });
      // Initialize drafts
      const drafts = {};
      (p.sections || []).forEach(s => {
        drafts[s.id] = s.content || '';
      });
      setSectionDrafts(drafts);
    } catch (e) {
      console.error('Error loading planification:', e);
      showSnackbar(t('planifications.errorLoadingPlanification'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const canSend = hasPermission('planifications.send') && plan && (plan.status === 'draft' || plan.status === 'needs_changes');
  const canReview = hasPermission('planification_review');

  const handleSave = async () => {
    if (!form.name.trim()) {
      showSnackbar(t('common.snackbar.nameRequired'), 'error');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await PlanificationService.update(id, {
          name: form.name,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          parent_id: form.parent_id || null,
          curso_id: form.curso_id || null,
        });
        showSnackbar(t('planifications.planificationUpdated'));
        await loadPlan();
      } else {
        if (!form.planification_type_id) {
          showSnackbar(t('planifications.typeRequired'), 'error');
          setSaving(false);
          return;
        }
        const payload = {
          planification_type_id: form.planification_type_id,
          name: form.name,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
        };
        if (form.parent_id) payload.parent_id = form.parent_id;
        if (form.curso_id) payload.curso_id = form.curso_id;
        const res = await PlanificationService.create(payload);
        const created = res.data?.data;
        showSnackbar(t('planifications.planificationCreated'));
        navigate(`/planifications/${created.id}/edit`);
      }
    } catch (e) {
      console.error('Error saving planification:', e);
      const msg = e.response?.data?.message || (e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(', ') : 'Error saving');
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!plan) return;
    try {
      await PlanificationService.send(plan.id);
      showSnackbar(t('planifications.planificationSent'));
      await loadPlan();
    } catch (e) {
      console.error('Error sending:', e);
      showSnackbar(e.response?.data?.message || 'Error sending', 'error');
    }
  };

  const handleStatus = async (status) => {
    if (!plan) return;
    try {
      await PlanificationService.setStatus(plan.id, status);
      showSnackbar(t('planifications.statusUpdated'));
      await loadPlan();
    } catch (e) {
      console.error('Error updating status:', e);
      showSnackbar(e.response?.data?.message || 'Error updating status', 'error');
    }
  };

  const handleSaveSection = async (sectionId) => {
    try {
      await PlanificationService.updateSection(sectionId, { content: sectionDrafts[sectionId] });
      showSnackbar(t('planifications.sectionSaved'));
      await loadPlan();
    } catch (e) {
      console.error('Error saving section:', e);
      showSnackbar(t('planifications.errorSavingSection'), 'error');
    }
  };

  const handleAddComment = async (sectionId) => {
    const comment = (commentDrafts[sectionId] || '').trim();
    if (!comment) {
      showSnackbar(t('planifications.commentRequired'), 'error');
      return;
    }

    try {
      await PlanificationService.addSectionComment(sectionId, comment, !!commentNeedsChanges[sectionId]);
      showSnackbar(t('planifications.commentAdded'));
      setCommentDrafts(prev => ({ ...prev, [sectionId]: '' }));
      setCommentNeedsChanges(prev => ({ ...prev, [sectionId]: false }));
      await loadPlan();
    } catch (e) {
      console.error('Error adding comment:', e);
      showSnackbar(t('planifications.errorAddingComment'), 'error');
    }
  };

  const handleUploadPlanAttachment = async (file) => {
    if (!plan || !file) return;
    try {
      await PlanificationService.uploadAttachment(plan.id, file);
      showSnackbar(t('planifications.fileUploaded'));
      await loadPlan();
    } catch (e) {
      console.error('Error uploading file:', e);
      showSnackbar(t('planifications.errorUploadingFile'), 'error');
    }
  };

  const handleUploadSectionAttachment = async (sectionId, file) => {
    if (!file) return;
    try {
      await PlanificationService.uploadSectionAttachment(sectionId, file);
      showSnackbar('Section file uploaded');
      await loadPlan();
    } catch (e) {
      console.error('Error uploading section file:', e);
      showSnackbar(t('planifications.errorUploadingFile'), 'error');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await PlanificationService.deleteAttachment(attachmentId);
      showSnackbar('Attachment deleted');
      await loadPlan();
    } catch (e) {
      console.error('Error deleting attachment:', e);
      showSnackbar('Error deleting attachment', 'error');
    }
  };

  const assignedList = useMemo(() => {
    const list = plan?.assignedUsers ?? plan?.assigned_users;
    return Array.isArray(list) ? list : [];
  }, [plan]);
  const assignmentsAvailable = isEdit && plan && (Array.isArray(plan?.assignedUsers) || Array.isArray(plan?.assigned_users));

  const buildAssignmentsPayload = (list) =>
    list.map((u) => ({ user_id: u.id, permission: u.pivot?.permission || 'view' }));

  const handleAddAssignment = async () => {
    if (!plan?.id || !addUserId) return;
    const current = buildAssignmentsPayload(assignedList);
    if (current.some((a) => Number(a.user_id) === Number(addUserId))) {
      showSnackbar(t('planifications.alreadyAssigned'), 'info');
      return;
    }
    setAssignmentSaving(true);
    try {
      await PlanificationService.updateAssignments(plan.id, [
        ...current,
        { user_id: Number(addUserId), permission: addPermission },
      ]);
      showSnackbar(t('planifications.assignmentsSaved'));
      setAddUserId('');
      setAddPermission('view');
      await loadPlan();
    } catch (e) {
      console.error('Error updating assignments:', e);
      showSnackbar(e.response?.data?.message || 'Error saving assignments', 'error');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleRemoveAssignment = async (userId) => {
    if (!plan?.id) return;
    const newList = assignedList.filter((u) => Number(u.id) !== Number(userId)).map((u) => ({ user_id: u.id, permission: u.pivot?.permission || 'view' }));
    setAssignmentSaving(true);
    try {
      await PlanificationService.updateAssignments(plan.id, newList);
      showSnackbar(t('planifications.assignmentsSaved'));
      await loadPlan();
    } catch (e) {
      console.error('Error removing assignment:', e);
      showSnackbar(e.response?.data?.message || 'Error saving assignments', 'error');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const planAttachments = useMemo(() => plan?.attachments || [], [plan]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/planifications')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{isEdit ? t('planifications.editPlanification') : t('planifications.newPlanification')}</Typography>
              {plan?.status && (
                <Chip
                  label={({ draft: t('planifications.statusDraft'), sent: t('planifications.statusSent'), in_review: t('planifications.statusInReview'), needs_changes: t('planifications.statusNeedsChanges'), approved: t('planifications.statusApproved') }[plan.status] || plan.status)}
                  color={statusColor(plan.status)}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isEdit && canSend && (
                <Button variant="outlined" startIcon={<SendIcon />} onClick={handleSend}>
                  {t('planifications.send')}
                </Button>
              )}
              {isEdit && canReview && plan?.status === 'sent' && (
                <Button variant="outlined" startIcon={<ReviewIcon />} onClick={() => handleStatus('in_review')}>
                  {t('planifications.startReview')}
                </Button>
              )}
              {isEdit && canReview && plan?.status === 'in_review' && (
                <>
                  <Button variant="outlined" color="error" startIcon={<NeedsChangesIcon />} onClick={() => handleStatus('needs_changes')}>
                    {t('planifications.needsChanges')}
                  </Button>
                  <Button variant="outlined" color="success" startIcon={<ApproveIcon />} onClick={() => handleStatus('approved')}>
                    {t('planifications.approve')}
                  </Button>
                </>
              )}
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {t('common.save')}
              </Button>
            </Box>
          }
        />
        <CardContent>
          {!isEdit && (
            <>
              {preselectedParentId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('planifications.parentHelperCreate', { name: parentPlan?.name ?? `Planification #${preselectedParentId}` })}
                </Alert>
              )}
              {!preselectedTypeId && !preselectedParentId ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('planifications.infoSelectType')}
                </Alert>
              ) : preselectedTypeId && !preselectedParentId ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('planifications.typeSelectedBelow', { name: types.find(ty => ty.id === form.planification_type_id || ty.id === Number(form.planification_type_id))?.name ?? '—' })}
                </Typography>
              ) : null}
            </>
          )}
          <Grid container spacing={2}>
            {!isEdit && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('planifications.typeOfPlanification')}</InputLabel>
                  <Select
                    label={t('planifications.typeOfPlanification')}
                    value={form.planification_type_id}
                    onChange={(e) => setForm(prev => ({ ...prev, planification_type_id: e.target.value }))}
                  >
                    <MenuItem value="">
                      <em>{t('planifications.selectTypePlaceholder')}</em>
                    </MenuItem>
                    {types.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                  {!preselectedTypeId && (
                    <FormHelperText>{t('planifications.typeHelper')}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            {isEdit && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('planifications.parentPlanification')}</InputLabel>
                  <Select
                    label={t('planifications.parentPlanification')}
                    value={form.parent_id ?? ''}
                    onChange={(e) => setForm(prev => ({ ...prev, parent_id: e.target.value || null }))}
                  >
                    <MenuItem value="">
                      <em>{t('planifications.noneTopLevel')}</em>
                    </MenuItem>
                    {planifications
                      .filter((pl) => pl.id !== plan?.id && (pl.parent_id == null || pl.parent?.id == null))
                      .map((pl) => (
                        <MenuItem key={pl.id} value={pl.id}>
                          {pl.name} {pl.type?.name ? `(${pl.type?.name})` : ''}
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>{t('planifications.parentHelper')}</FormHelperText>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('planifications.curso')}</InputLabel>
                <Select
                  label={t('planifications.curso')}
                  value={form.curso_id ?? ''}
                  onChange={(e) => setForm(prev => ({ ...prev, curso_id: e.target.value || null }))}
                >
                  <MenuItem value="">
                    <em>{t('planifications.none')}</em>
                  </MenuItem>
                  {cursos.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('planifications.cursoOptional')}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('planifications.startDate')}
                type="date"
                value={form.start_date || ''}
                onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('planifications.endDate')}
                type="date"
                value={form.end_date || ''}
                onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {isEdit && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('planifications.attachmentsTitle')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  component="label"
                >
                  {t('planifications.upload')}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) handleUploadPlanAttachment(f);
                    }}
                  />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {t('planifications.uploadFilesPlanification')}
                </Typography>
              </Box>

              {planAttachments.length === 0 ? (
                <Typography color="text.secondary">{t('planifications.noAttachments')}</Typography>
              ) : (
                <List dense>
                  {planAttachments.map(a => (
                    <ListItem key={a.id} divider>
                      <ListItemText
                        primary={a.original_name || a.path}
                        secondary={a.url ? a.url : a.path}
                        onClick={() => a.url && window.open(a.url, '_blank')}
                        sx={{ cursor: a.url ? 'pointer' : 'default' }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(a.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('planifications.shareWith')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('planifications.shareWithHelp')}
              </Typography>
              {!assignmentsAvailable ? (
                <Typography color="text.secondary">{t('planifications.assignmentsNotAvailable')}</Typography>
              ) : (
                <>
                  {assignedList.length === 0 ? (
                    <Typography color="text.secondary">{t('planifications.noAssignedUsers')}</Typography>
                  ) : (
                    <List dense sx={{ mb: 2 }}>
                      {assignedList.map((u) => (
                        <ListItem key={u.id} divider>
                          <ListItemText primary={u.name || u.email || `User #${u.id}`} />
                          <Chip size="small" label={u.pivot?.permission === 'edit' ? t('planifications.permissionEdit') : t('planifications.permissionView')} sx={{ mr: 1 }} />
                          <IconButton size="small" color="error" onClick={() => handleRemoveAssignment(u.id)} disabled={assignmentSaving}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>{t('planifications.addUser')}</InputLabel>
                      <Select
                        label={t('planifications.addUser')}
                        value={addUserId}
                        onChange={(e) => setAddUserId(e.target.value)}
                        disabled={assignmentSaving}
                      >
                        <MenuItem value="">
                          <em>{t('planifications.addUser')}</em>
                        </MenuItem>
                        {users
                          .filter((u) => !assignedList.some((a) => Number(a.id) === Number(u.id)))
                          .map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.name || u.email || `User #${u.id}`}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>{t('planifications.permissionLabel')}</InputLabel>
                      <Select
                        label={t('planifications.permissionLabel')}
                        value={addPermission}
                        onChange={(e) => setAddPermission(e.target.value)}
                        disabled={assignmentSaving}
                      >
                        <MenuItem value="view">{t('planifications.permissionView')}</MenuItem>
                        <MenuItem value="edit">{t('planifications.permissionEdit')}</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={handleAddAssignment}
                      disabled={!addUserId || assignmentSaving}
                    >
                      {t('planifications.addUser')}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {isEdit && plan && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(plan.sections || []).map((section) => {
            const comments = section.comments || [];
            const attachments = section.attachments || [];

            return (
              <Paper key={section.id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6">{section.name}</Typography>
                  <Button size="small" variant="contained" onClick={() => handleSaveSection(section.id)}>
                    {t('planifications.saveSection')}
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label={t('planifications.content')}
                  value={sectionDrafts[section.id] ?? ''}
                  onChange={(e) => setSectionDrafts(prev => ({ ...prev, [section.id]: e.target.value }))}
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                    {t('planifications.sectionAttachments')}
                  </Typography>
                  <Button variant="outlined" startIcon={<UploadIcon />} component="label" size="small" sx={{ mb: 1 }}>
                    {t('planifications.upload')}
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) handleUploadSectionAttachment(section.id, f);
                      }}
                    />
                  </Button>

                  {attachments.length === 0 ? (
                    <Typography color="text.secondary">{t('planifications.noSectionAttachments')}</Typography>
                  ) : (
                    <List dense>
                      {attachments.map(a => (
                        <ListItem key={a.id} divider>
                          <ListItemText
                            primary={a.original_name || a.path}
                            secondary={a.url ? a.url : a.path}
                            onClick={() => a.url && window.open(a.url, '_blank')}
                            sx={{ cursor: a.url ? 'pointer' : 'default' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(a.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                    {t('planifications.comments')}
                  </Typography>

                  {comments.length === 0 ? (
                    <Typography color="text.secondary">{t('planifications.noCommentsYet')}</Typography>
                  ) : (
                    <List dense>
                      {comments.map(c => (
                        <ListItem key={c.id} alignItems="flex-start" divider>
                          <ListItemText
                            primary={c.user?.name ? `${c.user.name}` : t('planifications.reviewer')}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.primary">{c.comment}</Typography>
                                <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  {canReview && (
                    <Box sx={{ mt: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('planifications.addComment')}
                            value={commentDrafts[section.id] || ''}
                            onChange={(e) => setCommentDrafts(prev => ({ ...prev, [section.id]: e.target.value }))}
                            InputProps={{
                              startAdornment: <CommentIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              checked={!!commentNeedsChanges[section.id]}
                              onChange={(e) => setCommentNeedsChanges(prev => ({ ...prev, [section.id]: e.target.checked }))}
                            />
                            <Typography variant="body2">{t('planifications.setNeedsChanges')}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <Button variant="contained" size="small" onClick={() => handleAddComment(section.id)}>
                            {t('planifications.add')}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanificationForm;
